import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Segmented, Space, Switch, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import type { PvpLiveRange } from '@/types/pvp';
import { LiveReport } from './LiveReport';
import {
  DEFAULT_BOTS,
  DEFAULT_RANGE,
  LIVE_RANGES,
  REFRESH_MS,
  createLiveLoader,
  liveQuery,
  type LiveState,
  type LoadReason,
} from './model';

const { Title, Text } = Typography;

/** A hidden tab skips its polls; the next tick after it is shown again reads. */
const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Realtime PvP (Rivals) live view: online players, live and finished matches,
 * completion and 11 health checks, read every 15 s while the page is open.
 */
export const PvpLivePage: React.FC = () => {
  const [range, setRange] = useState<PvpLiveRange>(DEFAULT_RANGE);
  const [includeBots, setIncludeBots] = useState(DEFAULT_BOTS === 'include');
  const [state, setState] = useState<LiveState>({ data: null, loading: true, error: null });
  const loader = useMemo(
    () =>
      createLiveLoader(adminApi.pvp.live, (patch) =>
        setState((current) => ({ ...current, ...patch }))
      ),
    []
  );

  const load = useCallback(
    (reason: LoadReason, nextRange: PvpLiveRange = range, nextBots: boolean = includeBots) =>
      loader.load(liveQuery(nextRange, nextBots), reason),
    [loader, range, includeBots]
  );

  // First load only: later reads come from the timer, a range or bots change, or Refresh.
  useEffect(() => {
    void loader.load(liveQuery(DEFAULT_RANGE, DEFAULT_BOTS === 'include'), 'load');
  }, [loader]);

  // useAutoRefresh keeps the newest callback, so a poll reads the current range and bots.
  const poll = useCallback(() => (pageHidden() ? undefined : load('poll')), [load]);
  useAutoRefresh(poll, true, REFRESH_MS);

  return (
    <div>
      <Title level={3} style={{ marginBottom: 0 }}>
        PvP Live
      </Title>
      <Text type="secondary">
        What the Realtime PvP (Rivals) servers are doing now: players online, live and finished
        matches, completion and server health. Reads again every 15 s; times are IST.
      </Text>

      <Space wrap style={{ margin: '12px 0', width: '100%', justifyContent: 'space-between' }}>
        <Space wrap size={16}>
          <Segmented
            options={LIVE_RANGES.map(({ label, value }) => ({ label, value }))}
            value={range}
            onChange={(v) => {
              const next = v as PvpLiveRange;
              setRange(next);
              void load('manual', next);
            }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={includeBots}
              onChange={(checked) => {
                setIncludeBots(checked);
                void load('manual', range, checked);
              }}
            />
            <span>Include bot matches</span>
          </label>
          <Text type="secondary" style={{ fontSize: 12 }}>
            (changes the match numbers and the server-ended check only)
          </Text>
        </Space>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => void load('manual')}
          loading={state.loading}
        >
          Refresh
        </Button>
      </Space>

      <LiveReport state={state} />
    </div>
  );
};

export default PvpLivePage;
